import { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import { Camera, Video, ImageOff, Download } from "lucide-react";
import api from "@/lib/api";
import { toDownloadUrl, resolveMediaUrl } from "@/lib/download";

export default function MediaCenter() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/media", { params: { general_only: true } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  const photos = items.filter((m) => m.category !== "video");
  const videos = items.filter((m) => m.category === "video");

  return (
    <div data-testid="media-page">
      <PageHero eyebrow="Media Center" title="Photo & Video Gallery" subtitle="Highlights from NKF Bharat national events, training camps and international meets. All photos are watermarked and downloadable." />
      <section className="section-spacing nkf-container">
        <div className="flex items-center gap-3 mb-6 text-slate-500 text-xs uppercase tracking-widest">
          <Camera className="h-4 w-4" /> Photo Gallery
        </div>
        {photos.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="media-photos">
            {photos.map((m) => (
              <a
                key={m.id}
                href={toDownloadUrl(m.media_url, `${(m.title || "nkf-photo").replace(/\s+/g, "_")}_${m.id}.jpg`)}
                download
                data-testid={`media-photo-${m.id}`}
                className="federation-card relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-100 group block"
                title="Download watermarked photo"
              >
                <img src={resolveMediaUrl(m.media_url)} alt={m.title || ""} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-nkf-navy/70 to-transparent opacity-0 group-hover:opacity-100 transition flex items-end justify-between p-3 gap-2">
                  {m.title ? <div className="text-white text-xs uppercase tracking-widest line-clamp-2 flex-1">{m.title}</div> : <span className="flex-1" />}
                  <span className="inline-flex items-center gap-1 bg-white/95 text-nkf-navy text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded shrink-0">
                    <Download className="h-3 w-3" /> Download
                  </span>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <EmptyState label={loaded ? "No photos published yet" : "Loading…"} />
        )}

        <div className="mt-12 flex items-center gap-3 mb-6 text-slate-500 text-xs uppercase tracking-widest">
          <Video className="h-4 w-4" /> Video Library
        </div>
        {videos.length ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="media-videos">
            {videos.map((v) => (
              <div key={v.id} className="rounded-md overflow-hidden border border-slate-200 bg-nkf-navy">
                <video src={resolveMediaUrl(v.media_url)} controls className="w-full aspect-video bg-black" />
                <div className="px-3 py-2 flex items-center justify-between gap-2">
                  {v.title ? <div className="text-xs uppercase tracking-widest text-white/80 truncate">{v.title}</div> : <span />}
                  <a
                    href={toDownloadUrl(v.media_url, `${(v.title || "nkf-video").replace(/\s+/g, "_")}_${v.id}.mp4`)}
                    download
                    className="inline-flex items-center gap-1 bg-nkf-gold text-nkf-navy text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded"
                    data-testid={`media-video-download-${v.id}`}
                  >
                    <Download className="h-3 w-3" /> Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState label={loaded ? "No videos published yet" : "Loading…"} />
        )}
      </section>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-md py-16 flex flex-col items-center justify-center text-slate-400">
      <ImageOff className="h-8 w-8 mb-2" />
      <div className="text-xs uppercase tracking-widest">{label}</div>
    </div>
  );
}
